<script setup>
import { cloneDeep, isEmpty, isNull } from 'lodash'
import { computed, ref, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AlertsApiHandler from '@/api/alerts'
import {
  ALERT_NAME_MAX_LENGTH,
  ALERTS_EXCLUDED_CHANNELS as EXCLUDED_CHANNELS,
  FALLBACK_ALERT_DISPLAY_NAME
} from '@/utils/check-ins/alerts-helpers'
import {
  useTriggersModal,
  useParticipantsValidation,
  TRIGGER_MODAL_ENTITY_TYPES
} from '@/utils/check-ins/schedule-composables'
import {
  createNotificationsPayload,
  getDefaultNotificationsModel,
  normalizeName,
  parsePayloadToFormModel
} from '@/utils/check-ins/schedules-helpers'
import {
  createParticipantsPayload,
  DEFAULT_SCHEDULE_PARTICIPANTS
} from '@/utils/check-ins/schedules-participants'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { SAVING_INDICATOR_TYPES } from '@/utils/components-configurations/saving-indicator'
import { ALERT_ENTITY_KEYS, SCHEDULE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { NOTIFICATION_TYPES, showNotify } from '@/utils/notify'
import { numberOrNullProp, objectOrNullProp } from '@/utils/prop-validators'
import { isSomeOfResponseStatuses, RESPONSE_STATUSES } from '@/utils/response-statuses'

import AppDialog from '@/components/AppDialog'
import ScheduleNotificationsManagement from '@/components/check-ins/ScheduleNotificationsManagement'
import ScheduleParticipantsManagement from '@/components/check-ins/ScheduleParticipantsManagement'
import ModalFooterActions from '@/components/objectives/forms/ModalFooterActions'
import SavingIndicator from '@/components/SavingIndicator'
import AppDivider from '@/components/ui/AppDivider'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import Modal from '@/components/ui/Modal/Modal'

defineOptions({
  name: 'AlertModal'
})

const { NAME, ID, TYPE_ID } = ALERT_ENTITY_KEYS

const { t } = useI18n()

const props = defineProps({
  opened: {
    type: Boolean
  },

  alertId: {
    required: true,
    validator: v => numberOrNullProp(v)
  },

  scheduleReference: {
    default: null,
    validator: v => objectOrNullProp(v)
  }
})

const emit = defineEmits({
  'on-close': null,
  'reload-alerts': null
})

const alertGeneralData = ref(null)
const alertParticipants = ref(null)
const alertNotifications = ref({})

const {
  validate: validateParticipants,
  validationErrors,
  resetValidationErrors,
  removeError
} = useParticipantsValidation({
  formModel: alertParticipants
})

const setFormModels = ({ alertData = {}, replaceSnapshot = true } = {}) => {
  const { generalData, notificationsData, participantsData } = parsePayloadToFormModel({
    payload: alertData,
    excludedChannels: EXCLUDED_CHANNELS,
    excludedKeys: [SCHEDULE_ENTITY_KEYS.PARAMS, SCHEDULE_ENTITY_KEYS.REMINDERS]
  })

  alertGeneralData.value = {
    [NAME]: generalData.name
  }

  alertNotifications.value = cloneDeep(notificationsData)
  alertParticipants.value = cloneDeep(participantsData)

  if (replaceSnapshot) {
    alertPayloadSnapshot.value = {
      ...cloneDeep(getFullPayload())
    }
  }

  resetValidationErrors()
}

const applyEditState = async () => {
  const api = new AlertsApiHandler()

  try {
    isAlertLoading.value = true

    const alertData = await api.getAlertById({ id: props.alertId })

    setFormModels({ alertData })
  } catch (error) {
    handleError({ error })

    if (isSomeOfResponseStatuses({ error, statuses: [RESPONSE_STATUSES.NOT_FOUND] })) {
      showNotify({
        type: NOTIFICATION_TYPES.ERROR,
        title: error.response?.data || 'Trigger not found'
      })
    }
    emit('reload-alerts')
    onClose()
  } finally {
    isAlertLoading.value = false
  }
}

const DEFAULT_NOTIFICATIONS = getDefaultNotificationsModel(EXCLUDED_CHANNELS)

const DEFAULT_FORM_MODEL = {
  [NAME]: FALLBACK_ALERT_DISPLAY_NAME
}

const applyCreateState = () => {
  alertGeneralData.value = cloneDeep(DEFAULT_FORM_MODEL)
  alertNotifications.value = cloneDeep(DEFAULT_NOTIFICATIONS)
  alertParticipants.value = cloneDeep(DEFAULT_SCHEDULE_PARTICIPANTS)
  resetValidationErrors()

  alertPayloadSnapshot.value = {
    ...cloneDeep(getFullPayload())
  }

  if (!isNull(props.scheduleReference)) {
    setFormModels({ alertData: cloneDeep(props.scheduleReference), replaceSnapshot: false })
  }
}

const onBeforeEnter = () => {
  if (isEdit.value) {
    applyEditState()
  } else {
    applyCreateState()
  }
}

const sendCloseEvent = () => emit('on-close')

const {
  isSomeParticipantsDropdownOpen,
  modalStyles,
  payloadSnapshot: alertPayloadSnapshot,
  isEdit,
  showSuccessNotification,
  isSaving,
  closeModalOnSavingFinished,
  isConfirmCloseShow,
  hideConfirmCloseModal,
  onConfirmClose,
  onClose,
  savingIndicatorKey,
  savingIndicatorTopKey,
  onSavingStarted,
  onSavingFinished,
  isNameValid,
  isConfirmButtonDisabled,
  isDataLoading: isAlertLoading
} = useTriggersModal({
  opened: () => props.opened,
  payload: () => getFullPayload(),
  checkDataChangeExcludedKeys: [ID, TYPE_ID],
  entityId: () => props.alertId,
  entityType: TRIGGER_MODAL_ENTITY_TYPES.CHECKIN_ALERT,
  sendCloseEvent
})

const confirmButtonText = computed(() => {
  return isEdit.value ? t('action.save') : t('action.create')
})

const getFullPayload = () => {
  const name = normalizeName(alertGeneralData.value?.[NAME], ALERT_NAME_MAX_LENGTH)

  return {
    ...toValue(alertGeneralData),
    name,
    [SCHEDULE_ENTITY_KEYS.NOTIFICATION_CHANNEL_IDS]: createNotificationsPayload({
      ...toValue(alertNotifications),
      excludedChannels: EXCLUDED_CHANNELS
    }),
    [SCHEDULE_ENTITY_KEYS.PARTICIPANTS]: createParticipantsPayload({
      participants: toValue(alertParticipants)
    })
  }
}

watch(
  () => props.opened,
  newValue => {
    if (!newValue) {
      alertGeneralData.value = null
      alertNotifications.value = {}
      isNameValid.value = true
      alertParticipants.value = null
      alertPayloadSnapshot.value = null
    }
  }
)

const onConfirm = async () => {
  if (isConfirmButtonDisabled.value) return

  const isParticipantsValid = validateParticipants()

  if (!isParticipantsValid) return

  const api = new AlertsApiHandler()

  try {
    closeModalOnSavingFinished.value = !isEdit.value

    onSavingStarted()

    const requestMethod = isEdit.value
      ? payload => api.updateAlert({ id: props.alertId, ...payload })
      : payload => api.createAlert(payload)

    const payload = getFullPayload()

    const alert = await requestMethod(payload)

    showSuccessNotification(payload[NAME])

    emit('reload-alerts', {
      eventName: isEdit.value ? 'checkin alert updated' : 'checkin alert created',
      ...payload
    })

    if (isEdit.value) {
      setFormModels({ alertData: alert })
    }
  } catch (error) {
    closeModalOnSavingFinished.value = false
    handleError({ error })
  } finally {
    onSavingFinished()
  }
}
</script>

<template>
  <Modal
    :data-testid="$attrs['data-testid'] || null"
    :scrollable-content="false"
    :show="opened"
    :style="modalStyles"
    class="alm-Modal"
    manual-close
    size="lg-next"
    @close="onClose"
    @before-enter="onBeforeEnter"
  >
    <template #header>
      <AppTitle :level="2" class="alm-Title" disable-margin>
        {{ t('checkins.alerts.title_message') }}
      </AppTitle>
    </template>

    <template #footer>
      <div class="alm-Footer">
        <AppDivider class="alm-Footer_Divider" no-margin />

        <ModalFooterActions
          :disabled="isConfirmButtonDisabled"
          :loading="isSaving || isAlertLoading"
          hide-checkbox
          @close="onClose"
          @create="onConfirm"
        >
          <template #confirm-button-text> {{ confirmButtonText }}</template>
        </ModalFooterActions>
      </div>
    </template>

    <template #before-close>
      <SavingIndicator :ref="savingIndicatorKey" />
    </template>

    <template #loader>
      <SavingIndicator :ref="savingIndicatorTopKey" :type="SAVING_INDICATOR_TYPES.LINE" />
    </template>

    <div class="alm-Body">
      <ScheduleParticipantsManagement
        v-if="!isNull(alertParticipants)"
        v-model="alertParticipants"
        v-model:is-some-dropdown-opened="isSomeParticipantsDropdownOpen"
        :validation-errors="validationErrors"
        subtitle="alerts.participants.subtitle"
        title="alerts.participants.title"
        @remove-error="removeError"
      />

      <AppDivider border-width="2px" class="alm-Body_Divider" no-margin />

      <ScheduleNotificationsManagement
        v-if="!isEmpty(alertNotifications)"
        v-model:channels-attributes="alertNotifications.channelsAttributes"
        v-model:selected-channels="alertNotifications.selectedChannels"
        :excluded-channels="EXCLUDED_CHANNELS"
      />
    </div>
  </Modal>

  <portal to="modal-windows">
    <AppDialog
      :show="isConfirmCloseShow"
      :title="$t('confirm_modal.title')"
      :type="DIALOG_TYPES.WARNING"
      @on-close="hideConfirmCloseModal"
      @on-confirm="onConfirmClose"
    >
      {{ $t('confirm_modal.description') }}

      <template #confirm-btn-text>
        {{ $t('confirm.discard_btn') }}
      </template>
    </AppDialog>
  </portal>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/schedule-checkin-modal';

.alm-Modal {
  @extend %modal-styles;
  --modal-header-padding: #{$page-top-padding} #{$page-right-padding-next} 11px #{$page-left-padding-next};
}

.alm-Body {
  --tgm-modal-body-gap: 20px;
  @extend %modal-body-styles;
}

.alm-Footer {
  @extend %modal-footer-styles;
}

.alm-Footer_Divider {
  @extend %modal-footer-divider-styles;
}

.alm-Title {
  line-height: 32px;
}

.alm-Body_Divider {
  --divider-color: #{$grey-3-next};
}
</style>
